import { Quaternion, Vector3 } from "@babylonjs/core";

const JOINTS = 56;
const CHANNELS = 3;
const FRAME_VALUES = JOINTS * CHANNELS;
const ROOT_ALPHA = 0.22;
const DEFAULT_ROTATION_ALPHA = 0.34;
const ARM_ALPHA = 0.3;
const HAND_ALPHA = 0.22;
const FACE_ALPHA = 0.28;
const HAND_TREMOR_THRESHOLD = 0.018;
const WRIST_TREMOR_THRESHOLD = 0.026;

export class MotionFrameSmoother {
  private previousRoot: Vector3 | null = null;
  private previousRotations: Array<Quaternion | null> = Array.from({ length: JOINTS }, () => null);

  reset(): void {
    this.previousRoot = null;
    this.previousRotations = Array.from({ length: JOINTS }, () => null);
  }

  smooth(frame: Float32Array<ArrayBufferLike>): Float32Array<ArrayBuffer> {
    const output = new Float32Array(FRAME_VALUES);
    const root = new Vector3(finiteNumber(frame[0]), finiteNumber(frame[1]), finiteNumber(frame[2]));
    const smoothedRoot = this.previousRoot ? Vector3.Lerp(this.previousRoot, root, ROOT_ALPHA) : root;
    output[0] = smoothedRoot.x;
    output[1] = smoothedRoot.y;
    output[2] = smoothedRoot.z;
    this.previousRoot = smoothedRoot.clone();

    for (let joint = 1; joint < JOINTS; joint += 1) {
      const offset = joint * CHANNELS;
      const target = axisAngleToQuaternion(
        finiteNumber(frame[offset]),
        finiteNumber(frame[offset + 1]),
        finiteNumber(frame[offset + 2])
      );
      const previous = this.previousRotations[joint];
      const alpha = smoothingAlpha(joint, previous, target);
      const smoothed = previous ? Quaternion.Slerp(previous, target, alpha) : target;
      const axisAngle = quaternionToAxisAngle(smoothed);
      output[offset] = axisAngle.x;
      output[offset + 1] = axisAngle.y;
      output[offset + 2] = axisAngle.z;
      this.previousRotations[joint] = smoothed.clone();
    }
    return output;
  }
}

export function interpolateMotionFrames(
  from: Float32Array<ArrayBufferLike>,
  to: Float32Array<ArrayBufferLike>,
  amount: number
): Float32Array<ArrayBuffer> {
  const output = new Float32Array(FRAME_VALUES);
  const t = clamp(amount, 0, 1);
  output[0] = lerp(finiteNumber(from[0]), finiteNumber(to[0]), t);
  output[1] = lerp(finiteNumber(from[1]), finiteNumber(to[1]), t);
  output[2] = lerp(finiteNumber(from[2]), finiteNumber(to[2]), t);
  for (let joint = 1; joint < JOINTS; joint += 1) {
    const offset = joint * CHANNELS;
    const a = axisAngleToQuaternion(
      finiteNumber(from[offset]),
      finiteNumber(from[offset + 1]),
      finiteNumber(from[offset + 2])
    );
    const b = axisAngleToQuaternion(
      finiteNumber(to[offset]),
      finiteNumber(to[offset + 1]),
      finiteNumber(to[offset + 2])
    );
    const axisAngle = quaternionToAxisAngle(Quaternion.Slerp(a, b, t));
    output[offset] = axisAngle.x;
    output[offset + 1] = axisAngle.y;
    output[offset + 2] = axisAngle.z;
  }
  return output;
}

function smoothingAlpha(joint: number, previous: Quaternion | null, target: Quaternion): number {
  const base = joint >= 26 && joint <= 55
    ? HAND_ALPHA
    : joint >= 14 && joint <= 22
      ? ARM_ALPHA
      : joint >= 23 && joint <= 25
        ? FACE_ALPHA
        : DEFAULT_ROTATION_ALPHA;
  if (!previous) {
    return 1;
  }
  const delta = Quaternion.Inverse(previous).multiply(target).toAxisAngle().angle;
  if (joint >= 26 && joint <= 55 && Math.abs(delta) < HAND_TREMOR_THRESHOLD) {
    return base * 0.25;
  }
  if ((joint === 21 || joint === 22) && Math.abs(delta) < WRIST_TREMOR_THRESHOLD) {
    return base * 0.35;
  }
  return base;
}

function axisAngleToQuaternion(x: number, y: number, z: number): Quaternion {
  const angle = Math.hypot(x, y, z);
  if (angle < 1e-6) {
    return Quaternion.Identity();
  }
  return Quaternion.RotationAxis(new Vector3(x / angle, y / angle, z / angle), angle);
}

function quaternionToAxisAngle(value: Quaternion): Vector3 {
  const axisAngle = value.toAxisAngle();
  if (!Number.isFinite(axisAngle.angle) || axisAngle.angle < 1e-6) {
    return Vector3.Zero();
  }
  return axisAngle.axis.scale(axisAngle.angle);
}

function finiteNumber(value: number): number {
  return Number.isFinite(value) ? value : 0;
}

function lerp(from: number, to: number, amount: number): number {
  return from + (to - from) * amount;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
